<template>
  <div class="reports-placeholder p-2">
    <page-header class="mb-2" :title="$t('Reports')">
    </page-header>
    <general-loader v-if="loading" :loading="loading" wrapper-class="bg-secondary h-100"/>
    <div v-else class="card content-wrapper p-2 overflow-auto">
      <b-list-group>
        <b-list-group-item v-for="report in reports" :key="report.uuid"
                           class="d-flex justify-content-between align-items-center report-item">
          <div class="d-flex align-items-center">
            <span>
              {{ report.display_name }}
            </span>
            <div class="d-inline-block ml-2" v-if="report.description && report.description.length > 0">
              <font-awesome-icon :id="`report-info-icon-${report.uuid}`" class="hover-cursor" icon="info-circle"
                                 style="font-size: 1.4em"/>
              <b-popover :target="`report-info-icon-${report.uuid}`" triggers="hover" placement="bottom">
                {{ report.description }}
              </b-popover>
            </div>
          </div>
          <div>
            <b-tooltip :target="`report-generate-btn-${report.uuid}`" :title="$t('Generate Report')"/>
            <b-button @click="onReportGenerateCLick(report)" :id="`report-generate-btn-${report.uuid}`"
                      variant="primary"
                      size="sm" class="mr-2">
              <font-awesome-icon icon="download"/>
            </b-button>
            <b-tooltip :target="`report-configure-btn-${report.uuid}`" :title="$t('Configure Report')"/>
            <b-button @click="onReportConfigurationClick(report)" :id="`report-configure-btn-${report.uuid}`"
                      variant="primary" size="sm">
              <font-awesome-icon icon="cog"/>
            </b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>
    <report-configuration-modal/>
    <report-generation-modal/>
  </div>
</template>

<script>
  import PageHeader from "@/core/components/page-header/PageHeader";
  import {createNamespacedHelpers} from "vuex";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import ReportsService from "@/modules/reports/ReportsService";
  import ReportConfigurationModal from "@/modules/reports/ReportConfigurationModal";
  import {SOURCE_REPORTS_LIST} from "@/modules/constants";
  import ReportGenerationModal from "@/modules/reports/ReportGenerationModal";
  import i18n from "@/shared/i18n";

  const {mapActions, mapState} = createNamespacedHelpers('reports');

  export default {
    components: {
      ReportGenerationModal,
      ReportConfigurationModal,
      GeneralLoader,
      PageHeader
    },
    name: "Reports",
    computed: {
      ...mapState({
        loading: state => state.loading,
        reports: state => state.reports,
      }),
    },
    data() {
      return {}
    },
    methods: {
      ...mapActions(['getReports', 'showReportConfigurationModal', 'showReportGenerationModal']),
      async onReportGenerateCLick(report) {
        if (report.type === 'file_download' || report.type === 'serverless' || report.type === 'celery') {
          const nonRequiredFilters = report.available_filters.filter(i => !i.required)
          if (nonRequiredFilters.length > 0) {
            this.showReportGenerationModal({
              source: SOURCE_REPORTS_LIST,
              report
            })
          } else {
            let validationResponse = await ReportsService.validateReport(report.uuid);
            if (validationResponse.success) {
              if (report.type === 'file_download') {
                await ReportsService.generateAndDownloadReport(report.uuid);
              } else if (report.type === 'serverless') {
                await ReportsService.generateReport(report.uuid);
                this.$toast('info', i18n.t("Please wait until report is ready."));
              } else if (report.type === 'celery') {
                await ReportsService.generateReport(report.uuid);
                this.$toast('info', i18n.t("Please wait until report is ready."));
              }
            } else {
              this.$errorDialog(validationResponse.body.non_field_errors[0])
            }
          }
        }
      },
      async onReportConfigurationClick(report) {
        const requiredFilters = report.available_filters.filter(i => i.required)
        if (requiredFilters.length === 0) {
          this.$successDialog(this.$i18n.t("No configuration available for this report"), this.$i18n.t("Configuration"))
        } else {
          this.showReportConfigurationModal({
            source: SOURCE_REPORTS_LIST,
            report
          })
        }
      }
    },
    beforeMount() {
      this.getReports()
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables.scss";

  .reports-placeholder {
    @include list-view-wrapper;
    display: flex;
    flex-direction: column;

    .content-wrapper {
      min-height: 20em;
    }
  }

  .report-item {
    &:hover {
      background-color: lighten($secondary, 5);
    }
  }
</style>
